'use client';

import {useCallback, useState, type SyntheticEvent} from 'react';
import {type z} from 'zod';

type Errors<T> = Partial<Record<keyof T, string | undefined>>;

export interface UseZodFormReturn<T> {
  values: T;
  errors: Errors<T>;
  setValue: <K extends keyof T>(key: K, value: T[K]) => void;
  handleBlur: (key: keyof T) => void;
  handleSubmit: (e?: SyntheticEvent) => void;
  setValues: (values: T) => void;
  reset: (values?: T) => void;
}

export interface UseZodFormOptions<T> {
  schema: z.ZodType<T>;
  initialValues: T;
  onSubmit: (values: T) => void | Promise<void>;
}

export function useZodForm<T extends Record<string, unknown>>({
  schema,
  initialValues,
  onSubmit,
}: UseZodFormOptions<T>): UseZodFormReturn<T> {
  const [values, setValuesState] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Errors<T>>({});

  const validate = useCallback(
    (vals: T): Errors<T> => {
      const result = schema.safeParse(vals);
      if (result.success) {
        return {};
      }
      const next: Errors<T> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof T | undefined;
        if (key !== undefined && next[key] === undefined) {
          next[key] = issue.message;
        }
      }
      return next;
    },
    [schema],
  );

  const setValue = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setValuesState(prev => ({...prev, [key]: value}));
    setErrors(prev => (prev[key] === undefined ? prev : {...prev, [key]: undefined}));
  }, []);

  const handleBlur = useCallback(
    (key: keyof T) => {
      setErrors(prev => ({...prev, [key]: validate(values)[key]}));
    },
    [validate, values],
  );

  const handleSubmit = useCallback(
    (e?: SyntheticEvent) => {
      e?.preventDefault();
      const allErrors = validate(values);
      setErrors(allErrors);
      if (Object.keys(allErrors).length === 0) {
        void onSubmit(values);
      }
    },
    [validate, values, onSubmit],
  );

  const setValues = useCallback((next: T) => {
    setValuesState(next);
  }, []);

  const reset = useCallback(
    (next?: T) => {
      setValuesState(next ?? initialValues);
      setErrors({});
    },
    [initialValues],
  );

  return {values, errors, setValue, handleBlur, handleSubmit, setValues, reset};
}
